import { DialogService } from '@app/services/dialog.service';
import { RuleService } from '@app/services/rule.service';
import * as kb3d from '@kb3d';
import {
    Configurator,
    eEnvironment,
    eHotspotAttach,
    eHotspotPosition,
    Field,
    Hotspot,
    IConfiguratorRuleArgs,
    IConfiguratorSession,
    IJsResult,
    IScene,
    ISceneLoadedData,
    ISceneRuleArgs,
    ITableObject,
    KbObjectManager,
    UiObject,
} from '@models';
import { IMessage, Utils } from '@tools';

/**
 * scope for the dynamically added annotation html layer
 */
export interface ISceneViewerScope {
    // extends ng.IScope {
    $digest: () => void;
    $apply: (fn: () => void) => void;
    hotspotClick?: ($event: ng.IAngularEvent, h: Hotspot) => void;
    hotspotCloseClick?: ($event: ng.IAngularEvent, h: Hotspot) => void;
    loaded?: boolean; //used to start the 'move me' animation
    arActive?: () => boolean;
    toggleAr?: () => void;
    supportsVr?: boolean;
    arEnabled?: boolean;
    fullscreenEnabled?: boolean;
    arLoading?: ng.IPromiseTracker;
    allHotspots?: Hotspot[]; //all of the hotspots to process (includes those from nested scenes)
}

export interface ILoadSceneArgs {
    isStandalone: boolean;
    scene: IScene;
    clearMemory: boolean;
    config: Configurator;
    isRenderContext: boolean;
    loadingExperience?: boolean;
}

/**
 * The constructor args of sceneViewer are designed such that they are not dependent on angular...
 * this is so it can be used on server-side renders without loading the whole UI framework.
 */
export interface ISceneViewerArgs {
    environment: eEnvironment;
    baseUrl: string;
    $q: ng.IQService;
    $timeout: <T>(fn: (...args: any[]) => T | ng.IPromise<T>, delay?: number) => void;
    //$injector: ng.auto.IInjectorService;
    ruleService: RuleService;
    dialogService: DialogService;
    //$compile: ng.ICompileService;
    compileTemplate: (hotspotElem: Element, scope: ISceneViewerScope) => HTMLElement | undefined;
    $scope: ISceneViewerScope;
    handleRuleError: (ruleName: string, result: IJsResult<IConfiguratorRuleArgs | ISceneRuleArgs>) => void;
    tableArraysDb: { [id: number]: ITableObject };
    //helper: ConfiguratorHelper;
    /** a promise telling the scene when the configurator has fully loaded */
    configuratorLoadedPromise: ng.IPromise<any>;
    /**
     * the css style selector for the element the viewer should be placed in
     */
    elemSelector: string;
    /**
     * if this is an embed, the request id holds an id from the parent page to identify
     * which embed is calling back (if they have multiple embeds on the same page)
     */
    requestId: string;
    /**
     * a callback for whenever a custom message has been sent by the scene
     */
    messageCallback: (msg: IMessage) => void;
    /**
     * a dictionary holding scene definitions for types of scenes that could be instantiated.
     * Needed for creating nested scenes
     */
    sceneDb: { [idScene: number]: IScene };
    /**
     * a delegate to call when selectPage has been called from the rules (configuratorController should handle this)
     */
    navigateToElement: (elem: UiObject) => void;

    configSession: IConfiguratorSession;

    arLoading: ng.IPromiseTracker;
    highlightColor: string;
    clientLanguage: string;
    isMobile: boolean;
    isRender: boolean;
    isOutput?: boolean;
    renderPass: string;
    assetRoot: string;
    clusterEnv: string;
    cacheBuster: string;
    lastDeploy: number;
    enableScreenshots?: boolean;
}

export abstract class SceneViewer {
    constructor(private args: ISceneViewerArgs) {
        Object.keys(args).forEach(prop => {
            this[prop] = args[prop];
        });
        this.isOutput = args.isOutput === true;
        this.loadDeferred = this.$q.defer<void>();
    }

    scene: IScene;
    sceneConfig: Configurator;
    /**
     * if the scene is attached to a full cpq configurator, this will be the cpq configurator,
     * otherwise, it will also be the scene configurator
     */
    uiConfig: Configurator;
    $scope: ISceneViewerScope;
    protected elem: HTMLElement;
    protected hotspotDiv: HTMLElement;
    protected loadDeferred: ng.IDeferred<void>;

    //constructor args-------------
    environment: eEnvironment;
    baseUrl: string;
    assetRoot: string;
    clusterEnv: string;
    cacheBuster: string;
    $q: ng.IQService;
    $timeout: ng.ITimeoutService;
    //$injector: ng.auto.IInjectorService;
    ruleService: RuleService;
    //$compile: ng.ICompileService;
    compileTemplate: (hotspotElem: Element, scope: ISceneViewerScope) => HTMLElement | undefined;
    handleRuleError: (ruleName: string, result: IJsResult<IConfiguratorRuleArgs | ISceneRuleArgs>) => void;
    tableArraysDb: { [id: number]: ITableObject };
    elemSelector: string;
    requestId: string;
    messageCallback: (msg: IMessage) => void;
    sceneDb: { [idScene: number]: IScene };
    navigateToElement: (elem: UiObject) => void;
    configSession: IConfiguratorSession;
    arLoading: ng.IPromiseTracker;
    highlightColor: string;
    clientLanguage: string;
    isMobile: boolean;
    isRender: boolean;
    isOutput: boolean;
    renderPass: string;
    configuratorLoadedPromise: ng.IPromise<any>;
    //end constructor args---------

    abstract init(): ng.IPromise<void>;
    abstract loadSceneInternal(args: ILoadSceneArgs): ng.IPromise<void>;
    abstract runRules(waitForLoad: boolean): ng.IPromise<any>;
    abstract clearHandlers();
    abstract runAction(actionName: string): ng.IPromise<IJsResult<ISceneRuleArgs>>;
    abstract runFieldAction(elem: UiObject): ng.IPromise<IJsResult<ISceneRuleArgs>>;
    abstract importUploadFieldImage(uiConfig: Configurator, field: Field, file?: File): ng.IPromise<any>;
    abstract resize();
    abstract snapshot(options: kb3d.ISnapshotArgs | clara2.ISnapshotOptions): Promise<Blob | string | void>;
    abstract dispose();

    public loadScene(args: ILoadSceneArgs): ng.IPromise<void> {
        // flush out stuff that needs to be flushed
        this.clearHandlers();
        this.$scope.loaded = false;
        this.loadDeferred = this.$q.defer<void>();

        // reset scope
        this.scene = args.scene;
        if (args.isStandalone) {
            //it's a standalone scene, so the uiConfig and sceneConfig are one and the same
            this.sceneConfig = args.config;
            this.uiConfig = args.config;
            this.sceneConfig.$uiConfigurator = args.config;
        } else {
            //it's a full cpq config, so we make another configurator just for the scene
            this.sceneConfig = new Configurator(new KbObjectManager(), this.scene.configurator);
            this.sceneConfig.$running = true;
            this.sceneConfig.name = this.scene.name;
            this.sceneConfig.idScene = this.scene.id;
            this.sceneConfig.$uiConfigurator = args.config;

            this.uiConfig = args.config;
            this.uiConfig.$sceneConfigurator = this.sceneConfig; //set the sceneConfigurator in the uiconfig so templates have access to it
        }

        return this.loadSceneInternal(args);
    }

    protected createElem() {
        let parentElem = document.querySelector(this.elemSelector) as HTMLElement;
        this.elem = document.createElement('div') as HTMLElement;
        this.elem.classList.add('kb-viewer__3d');
        parentElem.appendChild(this.elem);
    }

    protected createHotspotElem() {
        // load the hotspot div after the player so it ends up later in the html
        this.hotspotDiv = document.createElement('div') as HTMLElement;
        this.hotspotDiv.classList.add('kb-viewer__hotspots', 'xr-overlay');
        this.hotspotDiv.setAttribute('ng-include', "'hotspots-template'");
        this.hotspotDiv.setAttribute('ng-if', 'loaded');
        this.hotspotDiv.setAttribute('ng-cloak', '');
        this.elem.appendChild(this.hotspotDiv);
        this.hotspotDiv = this.compileTemplate(this.hotspotDiv, this.$scope);
    }

    public show() {
        if (this.elem.classList.contains('kb-hide')) {
            this.elem.classList.remove('kb-hide');

            // redraw otherwise it won't actually show
            this.resize();
        }
    }

    public hide() {
        if (!this.elem.classList.contains('kb-hide')) {
            this.elem.classList.add('kb-hide');
        }
    }

    protected notifyLoaded() {
        /* notify any outside pages that are embedding that the scene is loaded.  
        We pass the requestid that was passed in as a query string param back to 
        them so they know which iframe has been loaded (in case they have multiple 
        kbmax iframes on the same page) */
        Utils.sendMessageToParent({
            name: 'sceneloaded',
            data: {
                requestId: this.requestId,
            } as ISceneLoadedData,
        });
        Utils.sendMessageToParent({ name: 'progress', data: 0.2 });

        this.$scope.loaded = true;
        this.loadDeferred.resolve();
    }

    protected runActionRule(actionName: string, ruleArgs: ISceneRuleArgs): ng.IPromise<IJsResult<ISceneRuleArgs>> {
        let action = this.sceneConfig.actions.find(a => a.name.isEqual(actionName));
        if (action) {
            return this.loadDeferred.promise.then(() => {
                return this.ruleService.runRuleContainerAsync(action, ruleArgs).then(r => {
                    this.handleRuleError(action.name + ' ' + action.$type, r);
                    return r;
                });
            });
        }
    }

    protected setFieldsIfStandalone() {
        // if this is a standalone scene being used with a full CPQ configurator,
        // then we need to set the fields of the scene configurator
        if (this.sceneConfig != this.uiConfig && !this.sceneConfig.idProduct) {
            this.sceneConfig.setFields(this.uiConfig.getFieldsObject());
        }
    }

    public sendMessage(msg: IMessage) {
        if (this.messageCallback) {
            this.messageCallback(msg);
        }
        // send message to embed consumer
        Utils.sendMessageToParent(msg);
    }

    public refreshElementParent() {
        //get the first visible viewer element
        let $parent = $(this.elemSelector + ':visible:first');

        if ($parent.length && this.elem && !$parent.find(this.elem).length) {
            $parent[0].appendChild(this.elem);
        }
    }

    protected positionHotspot(pos: kb3d.IScreenBoundingBox, hotspot: Hotspot) {
        hotspot.$top = pos.y;
        hotspot.$left = pos.x;

        if (hotspot.popupPosition == eHotspotPosition.target) {
            let x = pos.x;
            let y = pos.y;

            if (hotspot.popupAttach == eHotspotAttach.top) {
                y = pos.top;
            } else if (hotspot.popupAttach == eHotspotAttach.bottom) {
                y = pos.top + pos.height;
            } else if (hotspot.popupAttach == eHotspotAttach.left) {
                x = pos.left;
            } else if (hotspot.popupAttach == eHotspotAttach.right) {
                x = pos.left + pos.width;
            } else if (hotspot.popupAttach == eHotspotAttach.topLeft) {
                y = pos.top;
                x = pos.left;
            } else if (hotspot.popupAttach == eHotspotAttach.topRight) {
                y = pos.top;
                x = pos.left + pos.width;
            } else if (hotspot.popupAttach == eHotspotAttach.bottomLeft) {
                y = pos.top + pos.height;
                x = pos.left;
            } else if (hotspot.popupAttach == eHotspotAttach.bottomRight) {
                y = pos.top + pos.height;
                x = pos.left + pos.width;
            }

            x += hotspot.popupOffsetX;
            y += hotspot.popupOffsetY;

            if (hotspot.keepPopupInViewer && hotspot.open) {
                let $popup = $('#kb-hotspot__popup-' + hotspot.id);
                let popupHeight = $popup.height();
                let popupWidth = $popup.width();

                if (y < 0) y = 0;
                if (y + popupHeight > this.hotspotDiv.clientHeight) {
                    y = Math.round(this.hotspotDiv.clientHeight - popupHeight);
                }
                if (x < 0) x = 0;
                if (x + popupWidth > this.hotspotDiv.clientWidth) {
                    x = Math.round(this.hotspotDiv.clientWidth - popupWidth);
                }
            }

            hotspot.popupTop = y.toString() + 'px';
            hotspot.popupLeft = x.toString() + 'px';
            hotspot.popupRight = 'auto';
            hotspot.popupBottom = 'auto';
        }
    }

    protected getAllHotspots(): Hotspot[] {
        let allHotspots = [];
        let allUiConfigs = [this.uiConfig].concat(this.uiConfig.getAllConfigurators());
        for (let uic of allUiConfigs) {
            if (uic.$sceneConfigurator && (uic == this.uiConfig || uic.$nestHotspots))
                allHotspots.pushArray(uic.$sceneConfigurator.hotspots);
        }
        return allHotspots;
    }
}
